/**
 * Single shared AudioContext for all UI sounds.
 *
 * Why not `new Audio(src)` per hook? Each HTMLAudioElement is its own
 * WebMediaPlayer; Chromium caps those around 75 per page. Lists with many
 * task rows blew through the limit because every TaskCheck mounted its own
 * Audio. Web Audio API uses one AudioContext + cached AudioBuffers + cheap
 * BufferSourceNodes per play — no per-mount media players, and overlapping
 * playback works.
 */

let ctx: AudioContext | null = null
const buffers = new Map<string, AudioBuffer>()
const inflight = new Map<string, Promise<AudioBuffer>>()

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (!ctx) {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctor) return null
    ctx = new Ctor()
  }
  return ctx
}

async function loadBuffer(src: string): Promise<AudioBuffer | null> {
  const cached = buffers.get(src)
  if (cached) return cached
  const pending = inflight.get(src)
  if (pending) return pending

  const c = getCtx()
  if (!c) return null

  const promise = (async () => {
    const res = await fetch(src)
    const arr = await res.arrayBuffer()
    const buf = await c.decodeAudioData(arr)
    buffers.set(src, buf)
    inflight.delete(src)
    return buf
  })()
  inflight.set(src, promise)
  try {
    return await promise
  } catch {
    inflight.delete(src)
    return null
  }
}

export function preloadSound(src: string): void {
  void loadBuffer(src)
}

export async function playSound(src: string, volume: number): Promise<void> {
  const c = getCtx()
  if (!c) return
  // Browser autoplay policy: context starts suspended until first user gesture.
  if (c.state === 'suspended') {
    try {
      await c.resume()
    } catch {
      return
    }
  }
  const buf = await loadBuffer(src)
  if (!buf) return

  const source = c.createBufferSource()
  source.buffer = buf
  const gain = c.createGain()
  gain.gain.value = volume
  source.connect(gain).connect(c.destination)
  source.start(0)
}
